<script lang="ts">
	import ThemeToggle from './ThemeToggle.svelte';
	import { getSiteNav, type SiteContext } from './site-nav';
	import type { SiteConfig } from './site-types';

	interface Props {
		site: SiteConfig;
		context: SiteContext;
		pathname?: string;
	}

	let { site, context, pathname = '' }: Props = $props();

	const items = $derived(getSiteNav(site, context, pathname));

	let mobileDialog = $state<HTMLDialogElement | null>(null);
	let menuOpen = $state(false);

	function openMenu() {
		if (!mobileDialog) return;
		mobileDialog.showModal();
		menuOpen = true;
		document.documentElement.classList.add('nav-open');
	}

	function closeMenu() {
		mobileDialog?.close();
		menuOpen = false;
		document.documentElement.classList.remove('nav-open');
	}

	function handleDialogClose() {
		menuOpen = false;
		document.documentElement.classList.remove('nav-open');
	}

	function handleDialogClick(event: MouseEvent) {
		if (event.target === mobileDialog) {
			closeMenu();
		}
	}

	function handleNavClick() {
		closeMenu();
	}
</script>

<a class="skip-link" href="#main">Skip to content</a>

<header class="site-header">
	<div class="container header-inner">
		<a class="brand" href={site.urls.home} aria-label="{site.name} home">
			<span class="brand-mark" aria-hidden="true"></span>
			<span class="brand-text">{site.name}</span>
		</a>

		<nav class="site-nav" aria-label="Primary">
			<ul class="nav-list">
				{#each items as item (item.href)}
					<li>
						<a
							class="nav-link"
							class:nav-cta={item.variant === 'cta'}
							class:nav-current={item.current}
							href={item.href}
							aria-current={item.current ? 'page' : undefined}
						>
							{item.label}
						</a>
					</li>
				{/each}
			</ul>
		</nav>

		<div class="header-actions">
			<ThemeToggle />

			<button
				class="menu-toggle"
				type="button"
				aria-expanded={menuOpen}
				aria-controls="mobile-nav"
				aria-haspopup="dialog"
				aria-label={menuOpen ? 'Close menu' : 'Open menu'}
				onclick={menuOpen ? closeMenu : openMenu}
			>
				<svg
					class="menu-icon"
					class:menu-icon-open={menuOpen}
					width="20"
					height="20"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					aria-hidden="true"
				>
					<line class="menu-line menu-line-top" x1="4" y1="7" x2="20" y2="7" />
					<line class="menu-line menu-line-mid" x1="4" y1="12" x2="20" y2="12" />
					<line class="menu-line menu-line-bot" x1="4" y1="17" x2="20" y2="17" />
				</svg>
			</button>
		</div>
	</div>
</header>

<dialog
	id="mobile-nav"
	class="mobile-nav-dialog"
	bind:this={mobileDialog}
	onclose={handleDialogClose}
	onclick={handleDialogClick}
	aria-label="Mobile navigation"
>
	<div class="mobile-nav-panel">
		<div class="mobile-nav-header">
			<span class="mobile-nav-title">Menu</span>
			<button class="mobile-nav-close" type="button" aria-label="Close menu" onclick={closeMenu}>
				<svg
					width="20"
					height="20"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					aria-hidden="true"
				>
					<line x1="18" y1="6" x2="6" y2="18" />
					<line x1="6" y1="6" x2="18" y2="18" />
				</svg>
			</button>
		</div>

		<nav aria-label="Mobile">
			<ul class="mobile-nav-list">
				{#each items as item (item.href)}
					<li>
						<a
							class="mobile-nav-link"
							class:nav-cta={item.variant === 'cta'}
							class:nav-current={item.current}
							href={item.href}
							aria-current={item.current ? 'page' : undefined}
							onclick={handleNavClick}
						>
							{item.label}
						</a>
					</li>
				{/each}
			</ul>
		</nav>
	</div>
</dialog>

<style>
	:global(html.nav-open) {
		overflow: hidden;
	}

	.skip-link {
		position: absolute;
		left: -9999px;
		top: 0;
		z-index: 100;
		padding: 0.75rem 1rem;
		background: var(--accent-fill);
		color: var(--accent-contrast);
		font-weight: 600;
		text-decoration: none;
		border-radius: var(--radius);
	}

	.skip-link:focus-visible {
		left: 1rem;
		top: 1rem;
	}

	.site-header {
		position: sticky;
		top: 0;
		z-index: 50;
		border-bottom: 1px solid var(--border);
		background: color-mix(in srgb, var(--bg) 88%, transparent);
	}

	@supports (backdrop-filter: blur(8px)) {
		.site-header {
			backdrop-filter: blur(10px);
		}
	}

	.header-inner {
		display: grid;
		grid-template-columns: 1fr auto 1fr;
		align-items: center;
		gap: 1rem;
		padding-block: 0.85rem;
	}

	.brand {
		justify-self: start;
		display: inline-flex;
		align-items: center;
		gap: 0.75rem;
		color: var(--text);
		text-decoration: none;
		font-weight: 600;
		letter-spacing: 0.01em;
	}

	.brand:hover,
	.brand:focus-visible {
		color: var(--text);
	}

	.brand-mark {
		width: 0.55rem;
		height: 0.55rem;
		border-radius: 50%;
		background: var(--accent);
		box-shadow: 0 0 0 4px var(--accent-muted);
		flex-shrink: 0;
	}

	.site-nav {
		justify-self: center;
	}

	.nav-list {
		display: flex;
		align-items: center;
		gap: 0.25rem;
		margin: 0;
		padding: 0;
		list-style: none;
	}

	.nav-link {
		display: inline-flex;
		align-items: center;
		padding: 0.45rem 0.75rem;
		border-radius: var(--radius);
		color: var(--text-muted);
		text-decoration: none;
		font-size: 0.95rem;
		font-weight: 500;
		transition:
			color 0.15s ease,
			background-color 0.15s ease;
	}

	.nav-link:hover,
	.nav-link:focus-visible {
		color: var(--text);
		background: var(--bg-subtle);
	}

	.nav-link.nav-cta {
		color: var(--accent);
	}

	.nav-link.nav-cta:hover,
	.nav-link.nav-cta:focus-visible {
		color: var(--accent-hover);
		background: var(--accent-muted);
	}

	.nav-link.nav-current,
	.mobile-nav-link.nav-current {
		color: var(--text);
		font-weight: 600;
	}

	.header-actions {
		justify-self: end;
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}

	.menu-toggle {
		display: none;
		align-items: center;
		justify-content: center;
		width: 2.5rem;
		height: 2.5rem;
		border: 1px solid var(--border-strong);
		background: var(--bg-elevated);
		color: var(--text);
		border-radius: var(--radius);
		cursor: pointer;
		transition:
			border-color 0.15s ease,
			background-color 0.15s ease;
	}

	.menu-toggle:hover,
	.menu-toggle:focus-visible {
		border-color: var(--accent);
		background: var(--bg-subtle);
	}

	.menu-icon-open .menu-line-top {
		transform: translateY(5px) rotate(45deg);
	}

	.menu-icon-open .menu-line-mid {
		opacity: 0;
	}

	.menu-icon-open .menu-line-bot {
		transform: translateY(-5px) rotate(-45deg);
	}

	.menu-line {
		transition:
			transform 0.2s ease,
			opacity 0.2s ease;
	}

	.mobile-nav-dialog {
		width: min(100%, 22rem);
		max-width: calc(100% - 2rem);
		margin: 0;
		margin-left: auto;
		height: 100%;
		max-height: none;
		padding: 0;
		border: none;
		border-left: 1px solid var(--border);
		background: var(--bg-elevated);
		color: var(--text);
	}

	.mobile-nav-dialog::backdrop {
		background: color-mix(in srgb, var(--bg) 40%, transparent);
		backdrop-filter: blur(2px);
	}

	.mobile-nav-panel {
		display: flex;
		flex-direction: column;
		height: 100%;
		padding: 1rem 0 1.5rem;
	}

	.mobile-nav-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 0 1.25rem 1rem;
		border-bottom: 1px solid var(--border);
	}

	.mobile-nav-title {
		font-size: 0.8rem;
		font-weight: 600;
		letter-spacing: 0.12em;
		text-transform: uppercase;
		color: var(--text-muted);
	}

	.mobile-nav-close {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 2.25rem;
		height: 2.25rem;
		border: 1px solid var(--border-strong);
		border-radius: var(--radius);
		background: var(--bg);
		color: var(--text);
		cursor: pointer;
	}

	.mobile-nav-close:hover,
	.mobile-nav-close:focus-visible {
		border-color: var(--accent);
	}

	.mobile-nav-list {
		margin: 0;
		padding: 0.5rem 0 0;
		list-style: none;
	}

	.mobile-nav-link {
		display: block;
		padding: 0.85rem 1.25rem;
		color: var(--text-muted);
		text-decoration: none;
		font-weight: 500;
		font-size: 1rem;
		transition:
			color 0.15s ease,
			background-color 0.15s ease;
	}

	.mobile-nav-link:hover,
	.mobile-nav-link:focus-visible {
		color: var(--text);
		background: var(--bg-subtle);
	}

	.mobile-nav-link.nav-cta {
		color: var(--accent);
	}

	@media (max-width: 720px) {
		.header-inner {
			grid-template-columns: 1fr auto;
		}

		.site-nav {
			display: none;
		}

		.menu-toggle {
			display: inline-flex;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.nav-link,
		.menu-toggle,
		.menu-line,
		.mobile-nav-link {
			transition-duration: 0.01ms;
		}
	}
</style>
